import { Action } from '@reduxjs/toolkit';
import { FormikValues, useFormik } from 'formik';
import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Dispatch } from 'redux';

import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';
import { ButtonTypes } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { ExpandableInput } from '@eika/expandable-input';
import { InputLabel } from '@eika/input';

import { Error } from '../../components/error/error';
import { VedleggError } from '../../components/error/vedleggError';
import { IdleTimerProviderMeldSkade } from '../../components/idle-timer-provider/idle-timer-provider-meld-skade';
import { useBankregnr } from '../../config';
import { useKunde } from '../../kundeinfo/kunde-context';
import { getEttersendingUrl } from '../../landingsside/deep-link-handler';
import { logger } from '../../logg/logger';
import { resetSavingState } from '../../meldskade/reducer/skademelding-actions';
import { AppState } from '../../reducers';
import { SavingState } from '../../reducers/savingState';
import { urler } from '../../router/router-urler';
import { ErrorCode } from '../../types/error-code-types';
import { EttersendingDTO, SkadeWebDTO } from '../../types/generated-types';
import { SideType, Skjemaside } from '../../utils/piwik-utils';
import {
    saveEttersending,
    saveEttersendingFailure,
    saveEttersendingSuccess,
    saveEttersendingUnauthorizedFailure,
    saveEttersendingVedleggFailure,
    sendEttersendingApi,
    setSkadenummer,
} from '../reducer/ettersending-actions';
import EditableEpost from './editable-epost';
import {
    EttersendingData,
    ettersendingSporsmal,
    generateEttersendingDTO,
    validationSchemaEttersending,
} from './ettersending-utils';
import FileUploaderEttersending from './file-uploader-ettersending';
import SkadeSelection from './skade-selection';

import './ettersending-form.scss';

const logWarning = (bankregnr: string, ettersendingDTO: EttersendingDTO, exception: Error) => {
    const log = logger(bankregnr);
    log.warn(
        `Failed to send in ettersending. Skadenummer: ${ettersendingDTO.skadeEllerForsikringsnummer.svar}, epost: ${
            ettersendingDTO.kundeEpost.svar
        }, vedlegg-liste: ${ettersendingDTO.vedlegg.vedleggIds.map(
            (vedleggId, index) => `${index}: id - ${vedleggId.id}, filename - ${vedleggId.fileName}. `,
        )}`,
        undefined,
        undefined,
        exception,
    );
};

export interface FormikProps {
    formikValues: FormikValues;
}

interface EttersendingProps {
    skader: SkadeWebDTO[];
    skadenummerFromQueryParam: string | undefined;
}

export const EttersendingForm: FC<EttersendingProps> = ({ skader, skadenummerFromQueryParam }) => {
    const dispatch: Dispatch<Action> = useDispatch();
    const navigate = useNavigate();
    const bankregnr = useBankregnr();
    const { kundeinfo } = useKunde();

    const { savingState, attachments } = useSelector((state: AppState) => state.ettersending);

    const [isVedleggLoading, setIsVedleggLoading] = useState<boolean>(false);

    const sideDetail: Skjemaside = { type: SideType.Skjemaside, skademeldingstype: 'ettersending' };

    const sendEttersending = (bankregnr: string, ettersendingData: EttersendingData) => {
        const ettersendingDTO: EttersendingDTO = generateEttersendingDTO(ettersendingData, attachments);
        const unauthorizedStatus = 401;

        dispatch(saveEttersending());

        sendEttersendingApi(ettersendingDTO, bankregnr)
            .then(() => {
                dispatch(saveEttersendingSuccess());
            })
            .catch((exception: RequestExceptionWithResponse) => {
                if (exception.status === unauthorizedStatus) {
                    dispatch(saveEttersendingUnauthorizedFailure());
                } else if (
                    [
                        ErrorCode.VEDLEGG_DOES_NOT_EXIST.valueOf(),
                        ErrorCode.UPLOADING_VEDLEGG_FAILED.valueOf(),
                        ErrorCode.UNEXPECTED_VEDLEGG.valueOf(),
                    ].includes(exception.code)
                ) {
                    logWarning(bankregnr, ettersendingDTO, exception);
                    dispatch(saveEttersendingVedleggFailure());
                } else {
                    logWarning(bankregnr, ettersendingDTO, exception);
                    dispatch(saveEttersendingFailure());
                }
            });
    };

    useEffect(() => {
        if (savingState === SavingState.Success) {
            dispatch(resetSavingState());
            navigate(urler().ettersendingKvitteringUrl);
        }
    }, [savingState, dispatch, navigate]);

    const onSubmit = (ettersendingData: EttersendingData) => {
        dispatch(setSkadenummer(ettersendingData.skadenummerEllerForsikringsnummer));
        sendEttersending(bankregnr, ettersendingData);
    };

    const initialValues: EttersendingData = {
        skadenummerEllerForsikringsnummer: skadenummerFromQueryParam,
        infoText: '',
        epost: kundeinfo?.epost || '',
    };

    const formik = useFormik({
        initialValues,
        validateOnBlur: true,
        validateOnChange: true,
        onSubmit: (values) => {
            onSubmit(values);
        },
        validationSchema: validationSchemaEttersending(),
    });

    const { values, handleSubmit, touched, errors, handleChange } = formik;

    return (
        <IdleTimerProviderMeldSkade skademeldingstype="ettersending">
            <form className="ettersending" onSubmit={handleSubmit}>
                <h2 className="ettersending__title">Oppdater saken din</h2>
                <div className="ettersending__form">
                    {!skadenummerFromQueryParam && <SkadeSelection skader={skader} formikValues={formik} />}
                    <FileUploaderEttersending attachmentsFromState={attachments} setIsLoading={setIsVedleggLoading} />
                    <div className="ettersending__info-input">
                        <InputLabel label={ettersendingSporsmal.infoText} htmlFor="ettersending__more-info">
                            <ExpandableInput
                                className="form-control"
                                id="ettersending__more-info"
                                value={values.infoText}
                                name="infoText"
                                onChange={handleChange}
                                error={touched.infoText ? errors.infoText : undefined}
                            />
                        </InputLabel>
                    </div>
                    <EditableEpost formikValues={formik} />

                    {savingState === SavingState.UgyldigVedlegg && <VedleggError />}
                    {savingState === SavingState.Failure && (
                        <Error
                            headerText="Her gikk noe galt – prøv igjen"
                            bodyText="Meldingen ble ikke sendt inn, prøv igjen. Dersom problemet gjentar seg flere ganger, kan du ringe oss på tlf 915&nbsp;03&nbsp;850."
                        />
                    )}
                    {savingState === SavingState.UnauthorizedUser && (
                        <Error
                            headerText="Du er ikke logget inn"
                            bodyText="Trykk på lenken under for å logge inn og starte skjema på nytt."
                            errorButtonProps={{
                                buttonText: 'Logg inn',
                                buttonDataTrackId: 'error-button-logg-inn',
                                sideDetail,
                                onButtonClick: () => window.location.replace(getEttersendingUrl()),
                            }}
                        />
                    )}
                    <div className="step-navigation">
                        <ButtonWithSpinner
                            buttonType={ButtonTypes.STANDARD_POSITIVE}
                            showSpinner={savingState === SavingState.Saving}
                            disabled={isVedleggLoading}
                            type="submit"
                            data-track-id="ettersending-button-submit"
                        >
                            Send inn
                        </ButtonWithSpinner>
                    </div>
                </div>
            </form>
        </IdleTimerProviderMeldSkade>
    );
};
